import moment from 'moment-timezone';
import { TTypedTFunction, useTranslation } from '@lib/useTypedTranslation';

import { formatValue } from '../../../../../../components/data-table/dataFormatters';
import { ITableColumn } from '../../../../../../components/data-table/types';

function formatTimestamp(deviceTimeZone: string, t: TTypedTFunction<'performance'>) {
  return (utc: number): string | JSX.Element => {
    const local = moment.utc(Number(utc)).tz(deviceTimeZone);
    return <div className='event-time'>{local.format('Do MMM')} {t('AT', { ns: 'performance' })} {local.format('LT')}</div>;
  };
}

// The Timestamp/Process Name/Product Name/Process Version/Product Version columns are shared by
// every process event type's Raw Data table (processHung, processCrashed, ...) - eventColumns
// appends whatever columns are specific to the caller's own event type (e.g. Reason for process
// hangs, Exception Code/Module for process crashes), matching that type's own info shape.
//
// Mirrors useProcessEventsSummaryColumns.tsx's own table-layout: auto fix: left unpinned, the
// table stretches to fill the examination card's full width (Bootstrap's .table sets width:
// 100%), spreading that leftover space across every column instead of concentrating it on
// Process Name/Product Name - the two columns most naturally suited to it (variable-length
// identifiers). Pinning every other, fixed-shape column (timestamp, both versions, and the
// caller's own type-specific columns - see processCrashesSection.tsx/processHangsSection.tsx) to
// width: '1%' (see IColumn.width) keeps them snug against their neighbours instead.
export const useProcessEventsRawColumns = (deviceTimeZone: string, eventColumns: ITableColumn[]) => {
  const { t } = useTranslation(['anomalyDetectionBeta', 'deviceProcesses', 'performance']);

  const columns: ITableColumn[] = [
    { id: 'timestamp', title: t('TIMESTAMP', { ns: 'anomalyDetectionBeta' }), dataPath: 'utc', sortable: true, processor: formatTimestamp(deviceTimeZone, t), width: '1%' },
    { id: 'processName', title: t('PROCESS_NAME', { ns: 'deviceProcesses' }), dataPath: 'processInfo.processName', sortable: true, processor: formatValue },
    { id: 'productName', title: t('PRODUCT_NAME', { ns: 'deviceProcesses' }), dataPath: 'processInfo.productName', sortable: true, processor: formatValue },
    { id: 'processVersion', title: t('PROCESS_VERSION', { ns: 'anomalyDetectionBeta' }), dataPath: 'processInfo.fileVersion', sortable: true, processor: formatValue, width: '1%' },
    { id: 'productVersion', title: t('PRODUCT_VERSION', { ns: 'deviceProcesses' }), dataPath: 'processInfo.productVersion', sortable: true, processor: formatValue, width: '1%' },
    ...eventColumns
  ];

  return columns;
};
